import { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { useSocketEvent, useSocketState } from '../helpers/useSocket'
import { findCiv } from '../helpers/drafting'
import { getBackendUrl } from '../helpers/url'
import { getLang } from '../helpers/i18n'
import { LiveGameMilitary } from '../components/LiveGame/LiveGameMilitary'
import { UnitShowcase } from '../components/UnitShowcase'
import { BonusShowcase } from '../components/BonusShowcase'

const isSniped = (draftOption) => draftOption?.action === 'sniped' || draftOption?.result === 'sniped'

// ABBA games hold both picks per side in the game civ draft; the one that is not sniped gets played
function getDraftCiv(civs, gameCivDraft) {
	const notSniped = gameCivDraft?.find((draftOption) => draftOption?.civ && !isSniped(draftOption))
	return notSniped?.civ ?? civs?.[0] ?? null
}

function getUpcomingCivs(match) {
	const game = match?.games?.find((g) => g.state === 'upcoming')
	if (!game) return null

	const isAbba = match.matchType?.includes('ABBA')
	return {
		left: isAbba ? getDraftCiv(game.leftCivs, game.leftGameCivDraft) : (game.leftCivs?.[0] ?? null),
		right: isAbba ? getDraftCiv(game.rightCivs, game.rightGameCivDraft) : (game.rightCivs?.[0] ?? null),
	}
}

// Live game sends labels, draft options can be aoe2cm names, so match on any known name
function findDescription(descriptions, civ) {
	if (!civ || !descriptions) return null
	const label = findCiv(civ)?.label ?? civ
	return descriptions.find((d) => d.label === label || d.label === civ || d.key === civ || d.name === civ) ?? null
}

// Text fields are either a plain string or translated per language code, e.g. { en, fr }
function localize(value, lang) {
	if (!value) return ''
	if (typeof value === 'string') return value
	return value[lang] || value.en || ''
}

export default function CivilizationCards() {
	const [config, setConfig] = useState({})
	const [themeConfig, setThemeConfig] = useState(null)
	const [match] = useSocketState('Match')
	const [gameState] = useSocketState('LiveGameOverlay')
	// null until toggled from the dashboard, so the out animation never plays on load
	const [showCards, setShowCards] = useState(null)
	//`?lang=fr`, defaults to en
	const lang = getLang()

	const fetchDescriptions = async () => {
		const res = await fetch(`${getBackendUrl()}/api/config/descriptions`)
		return res.json()
	}

	const { data: descriptions } = useQuery('descriptions', fetchDescriptions)

	useSocketEvent('updateConfig', (newdata) => {
		console.log(`Received updateConfig`, newdata)
		setConfig(newdata)
	})

	useSocketEvent('updateCivilizationCards', (value) => {
		setShowCards(value)
	})

	useEffect(() => {
		const themeName = config.theme ?? config.cssTheme?.replace('.css', '.json')
		if (!themeName) return
		fetch(`/themes/${themeName}`)
			.then((r) => r.json())
			.then(setThemeConfig)
			.catch(() => setThemeConfig(null))
	}, [config.theme, config.cssTheme])

	// An active live game wins over the upcoming game of the civ draft
	const hasLiveGame = !!(gameState?.leftPlayer?.civilization && gameState?.rightPlayer?.civilization)
	const activeCivs = hasLiveGame ? { left: gameState.leftPlayer.civilization, right: gameState.rightPlayer.civilization } : getUpcomingCivs(match)

	const cards = [
		{ side: 'left', civ: activeCivs?.left },
		{ side: 'right', civ: activeCivs?.right },
	].map((card) => ({ ...card, details: findCiv(card.civ), description: findDescription(descriptions, card.civ) }))

	const cssFiles = themeConfig?.css ?? (config.cssTheme ? [config.cssTheme] : [])
	const cssVars = Object.fromEntries(Object.entries(themeConfig?.cssVariables ?? {}).map(([k, v]) => [k.startsWith('--') ? k : `--${k}`, v]))

	const theme = themeConfig?.civilizationCards ?? {}
	const showDescription = theme.showDescription ?? false
	const showCivFocus = theme.showCivFocus ?? false
	const showTeamBonus = theme.showTeamBonus ?? false
	const showRegionalUnit = theme.showRegionalUnit ?? false
	const showUniqueTechs = theme.showUniqueTechs ?? false
	const showUniqueTechIcon = theme.showUniqueTechIcon ?? false
	const showDLC = theme.showDLC ?? false
	const showUniqueUnits = theme.showUniqueUnits ?? false
	const showUniqueBuildings = theme.showUniqueBuildings ?? false
	const showBonuses = theme.showBonuses ?? false
	const showLandmarks = theme.showLandmarks ?? false

	const visibility = showCards === null ? 'hidden' : showCards ? 'in' : 'out'

	return (
		<div className={`civilizationCards civilizationCards--${visibility}`} data-source={hasLiveGame ? 'liveGame' : 'draft'} style={cssVars}>
			{cssFiles.map((css) => (
				<link key={css} rel="stylesheet" type="text/css" href={`/themes/${css}`} />
			))}
			{cards.map(
				({ side, civ, details, description }) =>
					civ && (
						<div key={`${side}-${civ}`} className={`civilizationCards__card civilizationCards__card-${side}`}>
							{details?.originalFlagUrl && <img className="civilizationCards__flag" src={details.originalFlagUrl} alt="" />}
							<h1 className="civilizationCards__name">{description?.label ?? details?.label ?? civ}</h1>
							{showDescription && <p className="civilizationCards__description">{description?.en}</p>}
							{showDLC && <span className="civilizationCards__dlc">{description?.dlc}</span>}
							{showUniqueUnits && (
								<div className="civilizationCards__unitShowcases">
									{description?.uniqueUnits?.map((unit, index) => (
										<UnitShowcase key={index} icon={unit.icon} name={unit.en} />
									))}
								</div>
							)}
							{showBonuses && (
								<div className="civlizationCards__bonuses">
									{description?.bonuses?.map((unit, index) => (
										<BonusShowcase key={index} icon={unit.icon} name={unit.en} />
									))}
								</div>
							)}
						</div>
					),
			)}
		</div>
	)
}
